import { Stack, Tile, useTheme, type SecondarySurfaceColor } from "@carrotfertility/carotene-core"
import React from "react"

/**
 * A card in the consumer home's right-hand rail.
 *
 * ⚠ THE RAIL IS ONE OBJECT IN THE DESIGN AND TWO PACKAGES IN THE CODE: "Your Care Team" belongs to
 * `@internal/consumer` and "Appointments" to `@internal/telemedicine`, neither of which can import
 * the other. Held here so a padding change cannot land in one and leave two cards in the same
 * column at different insets.
 *
 * The design draws the card at 40px 24px 32px, each an exact token, and an internal rhythm of
 * 20-28px, whose nearest token is `lg`. The `sx` padding is what beats carotene's `Tile` theme
 * default — and `paddingInline` is `lg` there too, so that line changes nothing today. It is kept
 * deliberately: the design specifies 24px, and a future change to the `Tile` default should not
 * silently move the rail's inline padding with it.
 */
export function RailTile({
  color,
  children
}: {
  color: SecondarySurfaceColor
  children: React.ReactNode
}): JSX.Element {
  const { spacing, tokens } = useTheme()
  return (
    <Tile
      variant="secondary"
      color={color}
      sx={(theme) => ({
        paddingBlockStart: theme.spacing(theme.tokens.spacing.xxl),
        paddingInline: theme.spacing(theme.tokens.spacing.lg),
        paddingBlockEnd: theme.spacing(theme.tokens.spacing.xl)
      })}
    >
      <Stack spacing={spacing(tokens.spacing.lg)}>{children}</Stack>
    </Tile>
  )
}
